import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Server, Database, MessageCircle, Clock, Link2, ShieldCheck,
  AlertTriangle, RefreshCw, ExternalLink, Boxes, Workflow,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface OverviewData {
  architecture: {
    modules: number;
    endpoints: number;
    tables: number;
    migrations: { applied: number; latest: string | null };
    workers: string[];
  };
  whatsapp: {
    configured: boolean;
    sender: string | null;
    sent24h: number;
    failed24h: number;
    lastOutboundStatus: string | null;
    lastOutboundError: string | null;
    lastOutboundAt: number;
    lastInboundAt: number;
  };
  health: {
    errors24h: number;
    lastError: { level: string; source: string; message: string; created_at: number } | null;
    ordersToday: number;
    paidTodayCents: number;
  };
}

const fmtTime = (ts?: number | null) =>
  ts ? new Date(ts * 1000).toLocaleString('zh-HK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const TOPOLOGY: { layer: string; items: { name: string; desc: string }[] }[] = [
  {
    layer: '前端層（Cloudflare Pages）',
    items: [
      { name: 'React 18 SPA', desc: 'Vite 7 + Tailwind 3；dist/ 靜態托管，域名 hkspicyvalley.com' },
      { name: '路由單一數據源', desc: 'src/lib/appRoutes.tsx（顧客頁帶 label → 彈窗清單自動派生）' },
      { name: 'SPA fallback + 媒體代理', desc: 'functions/[[path]].ts（/media/* R2 代理，Range/206）' },
    ],
  },
  {
    layer: '後端層（Pages Functions + Hono）',
    items: [
      { name: '唯一 API 入口', desc: 'functions/api/[[route]].ts（91 行掛載器，top-level mount）' },
      { name: '41 個模組', desc: 'functions/modules/（認證/訂單/優惠/推薦/WhatsApp/辣友卡/洞見…）' },
      { name: '共用邏輯 lib/', desc: 'whatsapp・workflow・referral・orders・wanla・syslog・godfather…' },
    ],
  },
  {
    layer: '數據層（Cloudflare）',
    items: [
      { name: 'D1 x1493-db', desc: 'SQLite；migration 先 apply 再 deploy（鐵律）' },
      { name: 'KV ×2', desc: 'CMS_KV（驗證碼/設置快取）・SSE_KV（訂單確認橋接）' },
      { name: 'R2 x1493-assets', desc: '媒體/付款憑證/對話圖片；/media/{path} 對外' },
    ],
  },
  {
    layer: '外部依賴',
    items: [
      { name: 'CloudWAPI', desc: 'unofficial.cloudwapi.in——WhatsApp 收發一體（secrets 只存 CF store）' },
      { name: '入站 Webhook', desc: 'POST /api/webhooks/whatsapp（v10.0：2xx 回應體＝自動回覆，一律回空體）' },
    ],
  },
];

const SHORT_LINKS = [
  { path: '/t/:code', to: '/manuals/#角色', note: '培訓手冊分享碼（4 位）' },
  { path: '/gf/:code', to: '/godfather/{key}', note: '推廣活動碼（UTM 透傳）' },
  { path: '/wanla/:code', to: '辣友卡換領頁', note: '店員掃碼核銷' },
  { path: '/c/:key', to: '優惠詳情彈窗', note: '商家推廣用' },
  { path: '/p/:id', to: '產品詳情', note: '3 位短碼（開心分享）' },
  { path: '/sg/:slug', to: '/ref/d/{token}', note: '推薦人 Dashboard' },
  { path: '/m/:token', to: '/merchant/center?t=', note: '商家 magic link' },
  { path: '/s/:token', to: '/supplier/portal?token=', note: '供貨商 token' },
];

const AdminSystem: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'diagram'>('overview');

  const fetchOverview = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await apiFetch('/api/public/admin/system/overview');
      if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); return; }
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || '載入失敗');
      setData(json.data ?? json);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) { navigate('/admin'); return; }
    fetchOverview();
  }, []);

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) { localStorage.removeItem('admin_token'); navigate('/admin'); }
  };

  const wa = data?.whatsapp;
  const gatewayState =
    !wa?.configured ? { label: '未配置', cls: 'bg-gray-100 text-gray-600' }
    : (wa.failed24h >= 3 || wa.lastOutboundStatus === 'failed') ? { label: '異常', cls: 'bg-red-100 text-red-700' }
    : wa.sent24h > 0 ? { label: '正常', cls: 'bg-green-100 text-green-700' }
    : { label: '待觀察', cls: 'bg-amber-100 text-amber-700' };

  return (
    <AdminLayout currentPage="system" onLogout={handleLogout}>
      <div className="space-y-6">
        {/* 標題 */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-800 dark:text-white">
              <Server size={26} className="text-red-600" /> 系統架構
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              技術層面全局——拓撲・數據・通訊健康・排程，一頁睇晒。權威文檔：<code>docs/ARCHITECTURE-2026-08.md</code>＋附錄（78 表／281 端點）。
            </p>
          </div>
          <button
            onClick={fetchOverview}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> 重新整理
          </button>
        </div>

        {/* TAB 切換 */}
        <div className="flex w-fit gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-700">
          {([['overview', '📊 即時概況'], ['diagram', '🗺️ 系統架構圖']] as const).map(([t, label]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-lg px-4 py-2 text-sm font-bold transition ${tab === t ? 'bg-white text-red-600 shadow dark:bg-gray-800' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {err && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            載入失敗：{err}
          </div>
        )}

        {data && tab === 'overview' && (
          <>
            {/* 健康總覽卡 */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400"><MessageCircle size={14} /> WhatsApp 閘道</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${gatewayState.cls}`}>{gatewayState.label}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{wa?.sender || '—'}</span>
                </div>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  24h 發 {wa?.sent24h ?? 0} · 敗 {wa?.failed24h ?? 0}｜最後出站 {fmtTime(wa?.lastOutboundAt)}
                </div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400"><AlertTriangle size={14} /> 24h 系統錯誤</div>
                <div className={`mt-2 text-3xl font-bold ${data.health.errors24h > 0 ? 'text-red-600' : 'text-green-600'}`}>{data.health.errors24h}</div>
                <div className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400" title={data.health.lastError?.message || ''}>
                  {data.health.lastError ? `最近：[${data.health.lastError.source}] ${data.health.lastError.message}` : '暫無錯誤記錄'}
                </div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400"><Database size={14} /> 數據庫規模</div>
                <div className="mt-2 text-3xl font-bold text-gray-800 dark:text-white">{data.architecture.tables}<span className="text-base font-normal text-gray-400"> 表</span></div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {data.architecture.modules} 模組 · {data.architecture.endpoints} 端點 · migration {data.architecture.migrations.applied}（最新 {data.architecture.migrations.latest || '—'}）
                </div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400"><Boxes size={14} /> 今日營運</div>
                <div className="mt-2 text-3xl font-bold text-gray-800 dark:text-white">{data.health.ordersToday}<span className="text-base font-normal text-gray-400"> 單</span></div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">已確認收款 HK${(data.health.paidTodayCents / 100).toFixed(0)}</div>
              </div>
            </div>

            {/* 錯誤追蹤入口 */}
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/admin/syslog')} className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700">
                <ShieldCheck size={15} /> 系統日誌（自我修復數據源）
              </button>
              <button onClick={() => navigate('/admin/settings')} className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
                <ExternalLink size={14} /> WhatsApp 連線／測試發送
              </button>
            </div>

            {/* 拓撲 */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-gray-800 dark:text-white"><Workflow size={18} className="text-red-600" /> 部署拓撲（4 層）</h2>
              <div className="grid gap-4 lg:grid-cols-2">
                {TOPOLOGY.map((layer) => (
                  <div key={layer.layer} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                    <div className="mb-2 text-xs font-bold tracking-wide text-red-600">{layer.layer}</div>
                    <div className="space-y-2">
                      {layer.items.map((it) => (
                        <div key={it.name} className="text-sm">
                          <span className="font-medium text-gray-800 dark:text-gray-100">{it.name}</span>
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{it.desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Cron 排程 */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-gray-800 dark:text-white"><Clock size={18} className="text-red-600" /> 自動化排程（Workers）</h2>
              <div className="space-y-2">
                {data.architecture.workers.map((w) => (
                  <div key={w} className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-900 dark:text-gray-300">
                    <span className="h-2 w-2 rounded-full bg-green-500" /> {w}
                  </div>
                ))}
                <div className="px-3 text-xs text-gray-400">Worker 改動要喺 worker 目錄內獨立 <code>npx wrangler deploy</code>（Pages 部署唔會帶埋）</div>
              </div>
            </div>

            {/* 短連結家族 */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-gray-800 dark:text-white"><Link2 size={18} className="text-red-600" /> 短連結家族（file-based 302）</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      <th className="py-2 pr-4">路徑</th><th className="py-2 pr-4">去向</th><th className="py-2">備註</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {SHORT_LINKS.map((s) => (
                      <tr key={s.path}>
                        <td className="py-2 pr-4 font-mono font-bold text-red-600">{s.path}</td>
                        <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{s.to}</td>
                        <td className="py-2 text-xs text-gray-500 dark:text-gray-400">{s.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === 'diagram' && <SystemDiagram />}
      </div>
    </AdminLayout>
  );
};

// ============================================================
// 系統架構圖 TAB——視覺化 block diagram（純 CSS，免依賴）
// ============================================================

function LayerBox({ icon, title, sub, accent }: { icon: string; title: string; sub: string; accent?: boolean }) {
  return (
    <div className={`min-w-[150px] flex-1 rounded-xl border-2 p-3 ${accent ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'}`}>
      <div className="text-xl">{icon}</div>
      <div className="mt-1 text-sm font-bold leading-tight text-gray-800 dark:text-gray-100">{title}</div>
      <div className="mt-0.5 text-[10px] leading-snug text-gray-500 dark:text-gray-400">{sub}</div>
    </div>
  );
}

function FlowLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-center gap-2 py-1">
      <span className="text-gray-300 dark:text-gray-600">↓</span>
      <span className="rounded-full bg-gray-100 px-3 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">{children}</span>
      <span className="text-gray-300 dark:text-gray-600">↓</span>
    </div>
  );
}

function SystemDiagram() {
  const box = 'rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800';
  return (
    <div className="space-y-1">
      {/* ① 客戶觸點 */}
      <div className={box}>
        <div className="mb-3 text-xs font-bold uppercase tracking-widest text-red-600">① 客戶觸點</div>
        <div className="flex flex-wrap gap-3">
          <LayerBox icon="📱" title="WhatsApp 客人" sub="收推廣・確認落單・領券・查詢（CloudWAPI 雙向）" />
          <LayerBox icon="🌐" title="網店訪客" sub="瀏覽 79 產品・購物車・KPay 結帳・訂單查詢" />
          <LayerBox icon="🏪" title="店員／商家／夥伴" sub="掃碼核銷・商家中心・供貨商門戶・推薦看板" />
          <LayerBox icon="🤖" title="AIBOT（AI 員工）" sub="admin 通道 router 直送；wa-ai 頻道預埋（雙鎖未開）" />
        </div>
      </div>
      <FlowLabel>HTTPS 請求 ↑｜WhatsApp 入站 webhook ↓（POST 一律空體回應——v10.0 回應體＝自動回覆）</FlowLabel>

      {/* ② Cloudflare 邊緣 */}
      <div className="rounded-xl border-2 border-red-200 bg-red-50/40 p-5 dark:border-red-800 dark:bg-red-900/10">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-widest text-red-600">② Cloudflare 邊緣（hkspicyvalley.com）</div>
          <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-gray-500 dark:bg-gray-800">Pages＋Functions</span>
        </div>
        <div className="flex flex-wrap gap-3">
          <LayerBox icon="🖥️" title="React SPA 前台" sub="Vite 靜態托管・25 公開頁・40 admin 頁・GEO 全文層＋HTML 快取 1h" accent />
          <LayerBox icon="⚙️" title="Functions API" sub="Hono 唯一入口・41 模組・webhook 接收（空體回應）" accent />
          <LayerBox icon="🔗" title="短連結 302 家族" sub="/gf /wanla /c /p /sg /m /s /t——file-based" />
          <LayerBox icon="🖼️" title="/media 代理" sub="R2 直讀・Range/206 影片支援" />
        </div>
      </div>
      <FlowLabel>Bindings：DB（D1）・CMS_KV・SSE_KV・R2_BUCKET｜出站 → CloudWAPI／KPay／豐橋</FlowLabel>

      {/* ③ 數據層 */}
      <div className={box}>
        <div className="mb-3 text-xs font-bold uppercase tracking-widest text-red-600">③ 數據層</div>
        <div className="flex flex-wrap gap-3">
          <LayerBox icon="🗄️" title="D1 x1493-db" sub="78 表・281 端點・migration 先 apply 再 deploy" />
          <LayerBox icon="🔑" title="KV ×2" sub="CMS_KV（設置/GEO 快取/限流）・SSE_KV（訂單確認橋接）" />
          <LayerBox icon="📦" title="R2 x1493-assets" sub="產品圖・付款憑證・對話附件（ts_phone 前綴）" />
        </div>
      </div>
      <FlowLabel>排程讀寫（獨立部署於 Workers——改動須喺 worker 目錄另 deploy）</FlowLabel>

      {/* ④ 自動化 */}
      <div className={box}>
        <div className="mb-3 text-xs font-bold uppercase tracking-widest text-red-600">④ 自動化 Workers</div>
        <div className="flex flex-wrap gap-3">
          <LayerBox icon="⏰" title="workflow-cron" sub="*/15 提醒/佇列・01:00 日報+棄單關閉・T+2(72h/48h)・C6 回購・辣友卡・出站 15s 閘" accent />
          <LayerBox icon="📡" title="broadcast-cron" sub="廣播分批發送（防封速率）" />
        </div>
      </div>
      <FlowLabel>對外 API（secrets 只存 CF store；出站 button-first＋15s anti-block）</FlowLabel>

      {/* ⑤ 外部服務 */}
      <div className={box}>
        <div className="mb-3 text-xs font-bold uppercase tracking-widest text-red-600">⑤ 外部服務</div>
        <div className="flex flex-wrap gap-3">
          <LayerBox icon="💬" title="CloudWAPI" sub="unofficial.cloudwapi.in——WhatsApp 收發一體（per-device webhook）" />
          <LayerBox icon="💳" title="KPay" sub="全托管收銀台——webhook 自動確認收款（KPAY_LIVE=1）" />
          <LayerBox icon="🚚" title="順豐豐橋" sub="五接口＋webhook（憑證未申請＝dry-run）" />
          <LayerBox icon="🧠" title="DeepSeek" sub="日報敘事（素材 SQL 實取）" />
          <LayerBox icon="🤖" title="AIBOT（wtsAIBot）" sub="AI 員工系統——admin 通道 router 直送；wa-ai 頻道預埋（雙鎖未開）" />
        </div>
      </div>

      {/* 全鏈示例 */}
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <div className="mb-2 text-xs font-bold text-green-700 dark:text-green-300">💸 落單收款鏈（E2E 實證）</div>
          <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
            客人結帳 → KPay 收銀台 → 付款 → webhook → 自動確認（扣庫存＋計佣金＋標籤）→ WhatsApp 三按鈕確認信 → admin 群組知會
          </p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
          <div className="mb-2 text-xs font-bold text-amber-700 dark:text-amber-300">🎁 禮品閉環鏈（godfather）</div>
          <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
            活動落地 → 填表＋WhatsApp 驗證 → 標記寄出 → +72h（到店 +48h）收貨提醒 → 回貼連結 → 核實 → 社交證據入庫
          </p>
        </div>
      </div>
    </div>
  );
}

export default AdminSystem;
